"use client";

import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, usePathname } from "next/navigation";
import { useTransition, useEffect } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import {
  advanceFilterSchema,
  AdvanceFilterFormValues,
} from "@/components/schema/advertisements/filter";
import AdTypeSelect from "@/components/select/ad-type";
import PropertyTypeSelect from "@/components/select/property-type";
import TextField from "@/components/text-input";
import ListingTypeeSelect from "@/components/select/listing-type";
import Collapsible from "@/components/collapsible";
import Modal from "@/components/custom-modal";
import { useModal } from "@/hook/modal";
import { Button } from "@/components/button";

type Props = {
  initialValues: AdvanceFilterFormValues;
};

type FilterFormProps = Readonly<{
  initialValues: AdvanceFilterFormValues;
  showTitle?: boolean;
  onAfterSubmit?: () => void;
}>;

function FilterForm({
  initialValues,
  showTitle = true,
  onAfterSubmit,
}: FilterFormProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();

  const form = useForm<AdvanceFilterFormValues>({
    resolver: zodResolver(advanceFilterSchema),
    defaultValues: initialValues,
  });

  const { control, handleSubmit, reset } = form;

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const listing_type = useWatch({ control, name: "listing_type" });
  const titleValue = useWatch({ control, name: "title" });

  const onSubmit = (data: AdvanceFilterFormValues) => {
    const params = new URLSearchParams();

    Object.entries(data).forEach(([key, value]) => {
      if (
        value !== "" &&
        value !== undefined &&
        value !== null &&
        (typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean")
      ) {
        params.set(key, String(value));
      }
    });

    params.set("page", "1");

    startTransition(() => {
      router.push(`${pathname}?${params.toString()}`);
      onAfterSubmit?.();
    });
  };

  const handleReset = () => {
    const minimalParams = new URLSearchParams();

    // Reset keeps only ad_type; all other filters are cleared
    if (initialValues.ad_type)
      minimalParams.set("ad_type", initialValues.ad_type);

    startTransition(() => {
      router.push(`${pathname}?${minimalParams.toString() || ""}`);
      onAfterSubmit?.();
    });
  };

  const isSaleOrUnset =
    listing_type === "2" || listing_type === "" || listing_type === undefined;
  const isRent = listing_type === "1";

  return (
    <>
      {showTitle && (
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">فیلترها</h2>
          {isPending && (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
          )}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-1">
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <div
              className={`flex items-center gap-2 w-full px-3 py-2.5 mb-2 rounded-4xl border border-gray-300 bg-white ${
                titleValue ? "text-gray-900" : "text-gray-400"
              }`}
            >
              <input
                {...field}
                type="text"
                value={field.value ?? ""}
                placeholder="عنوان آگهی را وارد کنید..."
                className="w-full bg-transparent outline-none text-sm text-right placeholder:text-gray-400"
              />
              <Search
                size={20}
                className="shrink-0 text-gray-600 scale-x-[-1]"
              />
            </div>
          )}
        />

        {/* <Collapsible title="مناطق تحت پوشش" /> */}

        <Collapsible title="نوع ملک و معامله" defaultOpen>
          <Controller
            name="ad_type"
            control={control}
            render={({ field }) => (
              <AdTypeSelect
                {...field}
                value={field.value ?? ""}
                onChange={field.onChange}
                label="نوع آگهی"
                wrapperClassName="w-full"
                showClearButton={false}
              />
            )}
          />

          <Controller
            name="property_type"
            control={control}
            render={({ field }) => (
              <PropertyTypeSelect
                {...field}
                value={field.value ?? ""}
                onChange={field.onChange}
                label="نوع ملک"
                wrapperClassName="w-full"
                showClearButton={false}
              />
            )}
          />

          <Controller
            name="listing_type"
            control={control}
            render={({ field }) => (
              <ListingTypeeSelect
                {...field}
                value={field.value ?? ""}
                onChange={field.onChange}
                label="نوع معامله"
                wrapperClassName="w-full"
                showClearButton={false}
              />
            )}
          />
        </Collapsible>

        <Collapsible title="قیمت">
          {isSaleOrUnset && (
            <>
              <Controller
                name="min_price"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="حداقل قیمت (تومان)"
                    placeholder="برای مثال: 50,000,000"
                    wrapperClassName="w-full"
                    formatAsNumber
                    thousandSeparator=","
                    allowDecimal={false}
                    maxLength={20}
                  />
                )}
              />
              <Controller
                name="max_price"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="حداکثر قیمت (تومان)"
                    placeholder="برای مثال: 5,000,000,000"
                    wrapperClassName="w-full"
                    formatAsNumber
                    thousandSeparator=","
                    allowDecimal={false}
                    maxLength={20}
                  />
                )}
              />
            </>
          )}

          {isRent && (
            <>
              <Controller
                name="min_mortgage_amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="حداقل رهن (تومان)"
                    placeholder="برای مثال: 50,000,000"
                    wrapperClassName="w-full"
                    formatAsNumber
                    thousandSeparator=","
                    allowDecimal={false}
                    maxLength={20}
                  />
                )}
              />
              <Controller
                name="max_mortgage_amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="حداکثر رهن (تومان)"
                    placeholder="برای مثال: 5,000,000,000"
                    wrapperClassName="w-full"
                    formatAsNumber
                    thousandSeparator=","
                    allowDecimal={false}
                    maxLength={20}
                  />
                )}
              />
              <Controller
                name="min_rent_amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="حداقل اجاره (تومان)"
                    placeholder="برای مثال: 5,000,000"
                    wrapperClassName="w-full"
                    formatAsNumber
                    thousandSeparator=","
                    allowDecimal={false}
                    maxLength={20}
                  />
                )}
              />
              <Controller
                name="max_rent_amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="حداکثر اجاره (تومان)"
                    placeholder="برای مثال: 50,000,000"
                    wrapperClassName="w-full"
                    formatAsNumber
                    thousandSeparator=","
                    allowDecimal={false}
                    maxLength={20}
                  />
                )}
              />
            </>
          )}
        </Collapsible>

        <Collapsible title="ویژگی ها">
          <Controller
            name="min_area"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                label="حداقل متراژ (متر)"
                wrapperClassName="w-full"
                maxLength={6}
                placeholder="۸۰"
              />
            )}
          />

          <Controller
            name="max_area"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                label="حداکثر متراژ (متر)"
                wrapperClassName="w-full"
                maxLength={6}
                placeholder="۱۰۰"
              />
            )}
          />

          <Controller
            name="min_year_built"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                label="حداقل سال ساخت"
                wrapperClassName="w-full"
                maxLength={4}
                placeholder="۱۳۹۰"
              />
            )}
          />

          <Controller
            name="max_year_built"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                label="حداکثر سال ساخت"
                wrapperClassName="w-full"
                maxLength={4}
                placeholder="۱۴۰۰"
              />
            )}
          />
        </Collapsible>

        {/* <Collapsible title="امکانات" /> */}

        <div className="grid grid-cols-2 gap-3 mt-5">
          <Button
            variant="bare"
            type="button"
            text="پاک کردن فیلترها"
            onClick={handleReset}
            disabled={isPending}
            className="h-11 border border-primary text-primary font-medium rounded-4xl hover:bg-blue-50 transition-colors disabled:opacity-60 text-sm cursor-pointer"
          />

          <Button
            variant="bare"
            type="submit"
            text={isPending ? "در حال جستجو..." : "جستجو"}
            disabled={isPending}
            className="h-11 bg-primary hover:bg-blue-700 text-white font-medium rounded-4xl transition-colors disabled:opacity-60 text-sm cursor-pointer"
          />
        </div>
      </form>
    </>
  );
}

export default function AdvanceFilterSidebar({
  initialValues,
}: Readonly<Props>) {
  const { isModalOpen, handleOpen, handleClose } = useModal();

  return (
    <>
      {/* Desktop / large screens: inline sidebar */}
      <aside className="hidden lg:block w-full lg:w-80 xl:w-80 bg-white border border-gray-100 rounded-2xl shadow-xs p-5 lg:sticky lg:top-6 lg:self-start">
        <FilterForm initialValues={initialValues} />
      </aside>

      {/* Mobile / tablet: button + modal */}
      <div className="lg:hidden w-full">
        <Button
          variant="bare"
          type="button"
          text="فیلترها"
          onClick={handleOpen}
          iconPosition="start"
          iconNode={<SlidersHorizontal size={18} color="white" />}
          className="w-full h-11 flex items-center justify-center gap-2 bg-brand-700 border border-gray-200 rounded-4xl text-sm font-medium text-white shadow-xs hover:bg-gray-50 transition-colors cursor-pointer"
        />

        <Modal
          isOpen={isModalOpen}
          onClose={handleClose}
          title="فیلترها"
          className="w-full max-w-md max-h-[90vh] overflow-y-auto"
        >
          <FilterForm
            initialValues={initialValues}
            showTitle={false}
            onAfterSubmit={handleClose}
          />
        </Modal>
      </div>
    </>
  );
}
